import { createSlice, PayloadAction } from "@reduxjs/toolkit";
import KeyValueItem from "entities/KeyValueItem";
import LinkItem from "entities/LinkItem";
import { SigningTaskStatus } from "entities/SigningTaskStatus";
import Task from "entities/Task";
import i18n from "i18n";
import { apiCallBegan } from "store/api";
import { AppDispatch, AppState } from "store/configureStore";
import SliceCollectionBase from "store/types/SliceCollectionBase";
import SliceSingletonBase from "store/types/SliceSingletonBase";
import { authHeader, getWorkerId } from "store/users/auth";

interface TaskResponse {
    Id:number,
    Name:string,
    Description:string,
    Type:number,
    Deadline: Date | null,
    Regarding: string,
    Status: number,
    Link: string,
    Created: Date,
    SenderComment: string | null,
    Links: LinkItemResponse[],
    Files: LinkItemResponse[]
}

interface LinkItemResponse {
    Id: number,
    Url: string,
    Text: string
}

interface State {
    tasks:SliceCollectionBase<Task>,
    managerTasks:SliceCollectionBase<Task>,
    signingTaskStatus: SliceSingletonBase<SigningTaskStatus>,
}

interface KeyValueResponse {
    Id: number;
    Name: string;
}

const initialState: State = {
    tasks: {
    list: [],
    loading: false,
    lastFetch: null,
    error: null
    },
    managerTasks: {
    list: [],
    loading: false,
    lastFetch: null,
    error: null 
    },
    signingTaskStatus: {
        data: null,
        loading: false,
        lastFetch: null,
        error: null
    }
}

const slice = createSlice({
    name: "tasks",
    initialState,
    reducers: {
    tasksRequested: (tasks) => {
      tasks.tasks.loading = true;
      tasks.tasks.error = null;
    },
    tasksReceived: (tasks, action: PayloadAction<TaskResponse[]>) => {
      tasks.tasks.list = taskSchema(action.payload);
      tasks.tasks.loading = false;
      tasks.tasks.lastFetch = Date.now();
    },
    tasksRequestFailed: (tasks, action) => {
      tasks.tasks.loading = false;
      tasks.tasks.error = action.payload;
    },
    managerTasksRequested: (tasks) => {
      tasks.managerTasks.loading = true;
      tasks.managerTasks.error = null;
    },
    managerTasksReceived: (tasks, action: PayloadAction<TaskResponse[]>) => {
      tasks.managerTasks.list = taskSchema(action.payload);
      tasks.managerTasks.loading = false;
      tasks.managerTasks.lastFetch = Date.now();
    },
    managerTasksRequestFailed: (tasks, action) => {
      tasks.managerTasks.loading = false;
      tasks.managerTasks.error = action.payload;
    },
    signingTaskStatusRequested: (tasks) => {
        tasks.signingTaskStatus.loading = true;
        tasks.signingTaskStatus.error = null;
    },
    signingTaskStatusReceived: (tasks, action: PayloadAction<SigningTaskStatus>) => {
        tasks.signingTaskStatus.data = action.payload;
        tasks.signingTaskStatus.loading = false;
        tasks.signingTaskStatus.lastFetch = Date.now();
    },
    signingTaskStatusRequestFailed: (tasks, action) => {
        tasks.signingTaskStatus.loading = false;
        tasks.signingTaskStatus.error = action.payload;
    },
    statusUpdated: (tasks, action: PayloadAction<KeyValueResponse>) => {
        const index = tasks.tasks.list.findIndex(d => d.Id === action.payload.Id);
            if (index !== -1) {
                tasks.tasks.list[index].status = Number(action.payload.Name);
            }
    },
    managerStatusUpdated: (tasks, action: PayloadAction<KeyValueResponse>) => {
        const index = tasks.managerTasks.list.findIndex(d => d.Id === action.payload.Id);
            if (index !== -1) {
                tasks.managerTasks.list[index].status = Number(action.payload.Name);
            }
    }
  }
});

const {
  tasksRequested,
  tasksReceived,
  tasksRequestFailed,
  managerTasksRequested,
  managerTasksReceived,
  managerTasksRequestFailed,
  statusUpdated,
  managerStatusUpdated,
    signingTaskStatusRequested,
    signingTaskStatusReceived,
    signingTaskStatusRequestFailed,
} = slice.actions;

export const UpdateTaskState = (data: KeyValueItem) =>  
        async (dispatch: AppDispatch, getState: () => AppState) => {
        const workerId = getWorkerId(getState());
        await dispatch(apiCallBegan({
        url: `/api/tasks/${workerId}`,
        method: "patch",
        data: data,
        headers: {
            ...authHeader(),
            Accept: "application/json",
            "Content-Type": "application/json",
            "Accept-Language": i18n.language
        },
        onSuccess: statusUpdated.type,
    }));

        }

export const UpdateManagerTaskState = (data: KeyValueItem) =>
       async (dispatch: AppDispatch, getState: () => AppState) => {
             const workerId = getWorkerId(getState());
           await dispatch(apiCallBegan({
        url: `/api/tasks/${workerId}`,
        method: "patch",
        data: data,
        headers: {
            ...authHeader(),
            Accept: "application/json",
            "Content-Type": "application/json",
            "Accept-Language": i18n.language
        },
        onSuccess: managerStatusUpdated.type,
    }));

        }

export const getTasks = () => async (dispatch: AppDispatch, getState: () => AppState) => {
  if (getState().entities.tasks.tasks.loading) {
    return;
  }

  const workerId = getWorkerId(getState());

  const url = `api/tasks/worker/${workerId}`;

  var result =  dispatch(
      apiCallBegan({
          url,
          method: "GET",
          headers: { ...authHeader(), "Accept-Language": i18n.language },
          onStart: tasksRequested.type,
          onSuccess: tasksReceived.type,
          onError: tasksRequestFailed.type
      })
  );
  return result
}

export const getManagerTasks = () => async (dispatch: AppDispatch, getState: () => AppState) => {
  if (getState().entities.tasks.managerTasks.loading) {
    return;
  }

  const workerId = getWorkerId(getState());

  const url = `api/tasks/manager/${workerId}`;

  var result =  dispatch(
      apiCallBegan({
          url,
          method: "GET",
          headers: { ...authHeader(), "Accept-Language": i18n.language },
          onStart: managerTasksRequested.type,
          onSuccess: managerTasksReceived.type,
          onError: managerTasksRequestFailed.type
      })
  );
  return result
}

const taskSchema = (payload: TaskResponse[]): Task[] => {
    if (!payload || payload.length === 0) {
        return [];
    }

    return payload.map(
        (resp: TaskResponse): Task => mapTask(resp));
}

const mapTask = (resp: TaskResponse): Task => {
    return {
        Id: resp.Id,
        name: resp.Name,
        type: resp.Type,
        status: resp.Status,
        description: resp.Description,
        deadline: resp.Deadline,
        created: resp.Created,
        createdBy: resp.Regarding,
        link: resp.Link,
        senderComment: resp.SenderComment,
        links: resp.Links.map((item:LinkItemResponse): LinkItem => ({Id:item.Id, url:item.Url, text: item.Text})),
        files: resp.Files.map((item:LinkItemResponse): LinkItem => ({Id:item.Id, url:item.Url, text: item.Text}))
    }
}

export const startSigningProcessStatusPoll = (taskId: number) => async (dispatch: AppDispatch, getState: () => AppState) => {
     const workerId = getWorkerId(getState());
    const url = `api/tasks/startpoll/${taskId}/worker/${workerId}`;

    await dispatch(
        apiCallBegan({
            url,
            method: "POST",
            headers: { ...authHeader(), "Accept-Language": i18n.language },

        })
    );
}

export const getSigningProcessStatus = (taskId: number) => async (dispatch: AppDispatch, getState: () => AppState) => {
         const workerId = getWorkerId(getState());
    const url = `api/tasks/signingtaskstatus/${taskId}/${workerId}`;

    const result = await dispatch(
        apiCallBegan({
            url,
            method: "GET",
            headers: { ...authHeader(), "Accept-Language": i18n.language },
            onStart: signingTaskStatusRequested.type,
            onSuccess: signingTaskStatusReceived.type,
            onError: signingTaskStatusRequestFailed.type
        })
    );
    return result
}

export default slice.reducer;